import React, {FC} from "react";
import {EventConfigurationModal} from "../EventConfigurationModal";
import {FeeTypeDto} from "../../types/contracts";
import {MdOutlineDelete} from "react-icons/md";
import {FormFieldCheckboxes} from "./FormFieldCheckboxes";
import {useScrollRef} from "../../hooks/useScrollRef";
import {IoAddCircleOutline} from "react-icons/io5";
import {FormField} from "../../types/enums";
import {ALL_FORM_FIELDS} from "../../utils/constants";

type Props = {
    open: boolean;
    onClose: () => void;
    feeTypes: FeeTypeDto[];
    handleFeeTypeChange: (
        index: number,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    handleFeeCheckboxChange: (index: number, field: string) => void;
    removeFeeType: (index: number) => void;
    addFeeType: () => void;
    handleFormFieldsChange: (index: number, field: FormField) => void;
}

export const FeeTypesComponentModal:FC<Props> = ({open, onClose, feeTypes, handleFeeTypeChange, handleFeeCheckboxChange, removeFeeType, addFeeType, handleFormFieldsChange}) => {
    const {contentRef, handleScroll, lastItemRef} = useScrollRef(feeTypes.length);

    const handleAddFeeType = () => {
        addFeeType();
        handleScroll();
    };

    return (
        <EventConfigurationModal
            open={open}
            onClose={onClose}
            title="Typy poplatků"
            confirmText="Přidat poplatek"
            onConfirm={handleAddFeeType}
            contentRef={contentRef}
        >
            {feeTypes.length === 0 && (
                <div className="flex items-center justify-center gap-2 p-4 border border-gray-200 rounded-md cursor-pointer" onClick={handleAddFeeType}>
                    <IoAddCircleOutline />
                    <p>Přidejte první poplatek</p>
                </div>
            )}
            {feeTypes.map((feeType, index) => (
                <div
                    key={index}
                    className="mb-4 p-4 border border-gray-200 rounded-md"
                    ref={index === feeTypes.length - 1 ? lastItemRef : null}
                >
                    <div className="mb-2 flex items-start justify-between">
                        <label
                            className="block mb-2 font-semibold"
                            htmlFor={`feeType-name-${index}`}
                        >
                            Název poplatku
                        </label>
                        <button
                            type="button"
                            onClick={() => removeFeeType(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Odstranit ubytování"
                        >
                            <MdOutlineDelete size={20}/>
                        </button>
                    </div>
                    <textarea
                        id={`feeType-name-${index}`}
                        name="name"
                        value={feeType.name}
                        onChange={(e) => handleFeeTypeChange(index, e)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        required
                    />
                    <div className="mb-4">
                        <label
                            className="block mb-2 font-semibold"
                            htmlFor={`feeType-amount-${index}`}
                        >
                            Částka
                        </label>
                        <input
                            type="number"
                            id={`feeType-amount-${index}`}
                            name="amount"
                            value={feeType.amount}
                            onChange={(e) => handleFeeTypeChange(index, e)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                    <div className="mb-4 flex items-center">
                        <input
                            type="checkbox"
                            id={`feeType-requiresMembershipcard-${index}`}
                            checked={feeType.requiresMembershipCardNumber}
                            onChange={() =>
                                handleFeeCheckboxChange(index, "requiresMembershipCardNumber")
                            }
                            className="mr-2"
                        />
                        <label
                            htmlFor={`feeType-requiresMembershipcard-${index}`}
                            className="font-semibold"
                        >
                            Vyžaduje členskou kartu
                        </label>
                    </div>
                    <div className="mb-4 flex items-center">
                        <input
                            type="checkbox"
                            id={`feeType-allowsAdvancedinformations-${index}`}
                            checked={feeType.allowsAdvancedInformation}
                            onChange={() =>
                                handleFeeCheckboxChange(index, "allowsAdvancedInformation")
                            }
                            className="mr-2"
                        />
                        <label
                            htmlFor={`feeType-allowsAdvancedinformations-${index}`}
                            className="font-semibold"
                        >
                            Povolit pokročilé informace
                        </label>
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2 font-semibold">
                            Vyberte formulářová pole
                        </label>
                        <FormFieldCheckboxes
                            selectedFields={feeType.visibleFormFields || []}
                            onToggleField={(field) => handleFormFieldsChange(index, field)}
                            availableFields={ALL_FORM_FIELDS}
                            idPrefix={`feeType-formField-${index}`}
                        />
                    </div>
                </div>
            ))}
        </EventConfigurationModal>
    );
};